import { useEffect, useRef } from 'react';
import { tg, isTelegram } from './telegram';

interface MainButtonOpts {
  text: string;
  onClick: () => void;
  enabled?: boolean;
  visible?: boolean;
  progress?: boolean;
}

/**
 * Returns true when Telegram WebApp MainButton is actually available, so the
 * UI can hide its fallback "Submit" button to avoid duplication.
 */
export const hasTelegramMainButton = () => isTelegram();

/**
 * Native Telegram MainButton. Shows the button with given text and callback;
 * cleans up on unmount. No-op on non-Telegram environments (UI can show its
 * own fallback button there).
 *
 * onClick is captured via a ref so the latest closure is always used — this
 * prevents stale state (e.g. just-set client_id) from being seen by the
 * native button handler.
 */
export const useMainButton = (opts: MainButtonOpts) => {
  const { text, enabled = true, visible = true, progress = false } = opts;
  const cbRef = useRef(opts.onClick);
  cbRef.current = opts.onClick;

  useEffect(() => {
    const w = tg();
    if (!w) return;
    const mb = w.MainButton;
    const handler = () => cbRef.current?.();
    mb.setText(text);
    if (visible) mb.show();
    else mb.hide();
    if (enabled) mb.enable();
    else mb.disable();
    if (progress) mb.showProgress();
    else mb.hideProgress();
    mb.onClick(handler);
    return () => {
      mb.offClick(handler);
      mb.hide();
      mb.hideProgress();
    };
  }, [text, enabled, visible, progress]);
};

/**
 * Native Telegram BackButton. Calls `onBack` when pressed (typically navigate(-1)).
 */
export const useBackButton = (onBack: () => void, visible = true) => {
  useEffect(() => {
    const w = tg();
    if (!w) return;
    const bb = w.BackButton;
    if (visible) bb.show();
    else bb.hide();
    bb.onClick(onBack);
    return () => {
      bb.offClick(onBack);
      bb.hide();
    };
  }, [onBack, visible]);
};
